import { useParams, useNavigate } from 'react-router-dom';
import { usePermission } from '../../../hooks/usePermission';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { getCategoryById } from '../config/reportCatalog';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';

const CATEGORY_ICONS = {
  inventory: Inventory2OutlinedIcon,
  purchase: ShoppingCartOutlinedIcon,
  sales: PointOfSaleOutlinedIcon,
  financial: AccountBalanceOutlinedIcon,
  management: DashboardOutlinedIcon,
  compliance: GavelOutlinedIcon,
  'van-sales': DirectionsCarOutlinedIcon,
};

function ReportCategoryView() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermission();
  const category = getCategoryById(categoryId);

  const isReportAllowed = (reportId) => {
    return hasPermission('reports.reports.read') || hasPermission(`reports.${reportId}.read`);
  };

  if (!category) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Category not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/reports')} sx={{ mt: 2 }}>
          Back to Reports
        </Button>
      </Box>
    );
  }

  const allowedReports = (category.reports || []).filter(r => isReportAllowed(r.id));

  const CategoryIcon = CATEGORY_ICONS[category.id] || AssessmentIcon;

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reports')}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' },
          }}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 4,
          pb: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
          }}
        >
          <CategoryIcon sx={{ fontSize: 32 }} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}>
            {category.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {allowedReports.length} accessible reports
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {allowedReports.length === 0 ? (
          <Grid size={12}>
            <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary', textAlign: 'center', p: 4 }}>
              You do not have permission to view any reports in this category.
            </Typography>
          </Grid>
        ) : (
          allowedReports.map((report) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={report.id}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
              onClick={() => navigate(`/reports/view/${report.id}`)}
            >
              <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                    noWrap
                  >
                    {report.label}
                  </Typography>
                </Box>
                <ChevronRightRoundedIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
              </CardContent>
            </Card>
          </Grid>
        ))
      )}
      </Grid>
    </Box>
  );
}

export default ReportCategoryView;
